<template>
	<BasePublishDetail :root-category="true" v-slot="{item}" @data="postData">
		<CmsBreadcrumbs :items="item.breadcrumbs" />

		<div class="pd-header">
			<div class="pd-wrapper">
				<NuxtLink class="pd-category" :to="item.category_url_without_domain" v-if="item.category_title">{{ item.category_title }}</NuxtLink>
				<h1 class="pd-title">{{ item.seo_h1 }}</h1>
				<div class="pd-header-bottom">
					<ClientOnly>
						<BaseFeedbackRatesWidget :data="item.feedback_rate_widget" v-slot="{rate, stars}">
							<div class="pd-header-rate-section">
								<span class="cp-rate rates-container" v-html="stars" />
								<span class="pd-comment-average special">{{ rate }}</span>
							</div>
						</BaseFeedbackRatesWidget>

						<BaseFeedbackCommentsWidget :data="item.feedback_comment_widget" v-slot="{counter}">
							<div class="pd-comments">
								<a class="value" @click="scrollTo('#pd_comments_title', {offset: 90})" href="javascript:void(0)">
									<span class="label">{{ labels.get('show_comments') }}</span> <span class="value">({{ counter }})</span>
								</a>
							</div>
						</BaseFeedbackCommentsWidget>
					</ClientOnly>
				</div>
			</div>
		</div>

		<div class="wrapper">
			<div class="pd-hero-image" v-if="item.main_image">
				<BaseUiImage :data="item.main_image_thumbs?.['width1480-height540-crop1']" :alt="item.main_image_description" :title="item.main_image_title" />
			</div>
		</div>

		<div class="pd-cnt">
			<div class="pd-wrapper">
				<div class="pd-info">
					<NuxtLink class="btn btn-white pd-btn-all" :to="item.root_category.url_without_domain" v-if="item.root_category">
						<span>{{ labels.get('back_to_all_posts') }}</span>
					</NuxtLink>

					<div class="pd-date">
						<span class="value">{{ formatDate(item.datetime_published) }}</span>
					</div>
				</div>

				<div class="pd-short-desc intro-text" v-if="item.short_description" v-html="item.short_description" />

				<div class="pd-desc cms-content">
					<div v-if="item.content" v-html="item.content" v-interpolation />

					<ul class="pd-documents" v-if="item.documents">
						<li v-for="document in item.documents" :key="document.id">
							<a target="_blank" :href="document.url" :title="document.description">{{ document.title }}</a>
						</li>
					</ul>
				</div>

				<ClientOnly>
					<BaseUiImages v-if="item?.images?.length > 1" :images="item.images" v-slot="{items: images}">
						<div class="pd-thumbs" v-if="images">
							<a v-for="file in images.slice(1)" :key="file.id" rel="gallery" :title="file.title">
								<BaseUiImage loading="lazy" :data="file.file_thumbs?.['width920-height920']" :alt="file.description" />
							</a>
						</div>
					</BaseUiImages>
				</ClientOnly>

				<CmsShare />
			</div>
		</div>

		<div class="pd-wrapper">
			<BaseCatalogProductsWidget :fetch="{mode: 'widget', related_publish_id: item.id, only_available: true, limit: 8}" v-slot="{items}" @loadProductsWidget="loadProductsWidget">
				<div v-if="items.length" class="pd-related-products">
					<div class="pd-related-title">{{ labels.get('related_products') }}</div>
					<template v-for="(item, index) in items" :key="item.id">
						<CatalogIndexEntryFeatured :item="item" :index="index" :categoryListData="itemData" mode="publish" />
					</template>
				</div>
			</BaseCatalogProductsWidget>

			<div class="pd-tab-comment">
				<ClientOnly>
					<div class="pd-tab-comment-title" id="pd_comments_title">{{ labels.get('tab_comments') }}</div>
					<div class="cd-comments-info">
						<BaseFeedbackRatesWidget :data="item.feedback_rate_widget" v-slot="{rate, stars, counter}">
							<div class="cd-comments-info-col1" :class="{'empty': counter == 0}">
								<div class="cd-comments-rate-section">
									<span class="cp-rate rates-container" v-html="stars" />
									<span v-if="rate > 0" class="cd-comments-rate-average">{{ rate }}</span>
								</div>
								<div class="cd-comments-qty" v-if="counter > 0">
									{{ labels.get('comments_qty').replace('%c%', counter) }}
								</div>
								<div class="cd-comments-qty" v-else>{{ labels.get('no_comments_publish') }}</div>
								<button class="cd-comments-form-button" :class="{'active': commentsForm}" @click="commentsForm = !commentsForm">
									<span class="show">{{ labels.get('comments_show_form') }}</span>
									<span class="hide">{{ labels.get('comments_hide_form') }}</span>
								</button>
							</div>
							<div v-if="rate > 0" class="cd-comments-info-col2 cd-chart-items">
								<div class="cd-chart-item" v-for="rateChart in ratesChart" :key="rateChart.rate">
									<div class="cd-chart-rate">{{ rateChart.rate }}</div>
									<div class="cd-chart-bar"><span class="cd-chart-progress-bar" :style="{width: ((rateChart.counter / counter) * 100).toFixed(0) + '%'}"></span></div>
									<div class="cd-chart-qty">{{ rateChart.counter }}</div>
								</div>
							</div>
						</BaseFeedbackRatesWidget>
					</div>

					<div class="comments-form active" v-show="commentsForm">
						<BaseFeedbackCommentsForm class="form-comment form-animated-label" v-slot="{fields, onReset, status}">
							<template v-if="!status?.success">
								<BaseFormField v-for="item in fields" :key="item.name" :item="item" v-slot="{errorMessage, floatingLabel}">
									<p class="field comment-field" :class="['comment-field-' + item.name, {'ffl-floated': floatingLabel && item.name != 'rate', 'hidden': item.type == 'hidden'}]">
										<BaseFormInput />
										<BaseCmsLabel tag="label" :for="item.name" :code="item.name == 'message' ?  'form_comments_message' : item.name" />
										<span class="error" v-show="errorMessage" v-html="errorMessage" />
									</p>
								</BaseFormField>
								<div class="comment-button-container">
									<button class="btn btn-send-comment" type="submit">{{ labels.get('send_comment') }}</button>
									<div class="comment-form-note">{{ labels.get('comment_form_note') }}</div>
								</div>
							</template>

							<div class="comment-success" v-if="status?.success">
								<div v-html="labels.get('comment_success')" />
								<a href="javascript:void(0);" @click="onReset">{{ labels.get('comment_add_new') }}</a>
							</div>
						</BaseFeedbackCommentsForm>
					</div>
				</ClientOnly>

				<BaseFeedbackComments :items="item.feedback_comment_widget.items" v-slot="{items: comments}">
					<div class="comments-list comments_data comments-container" id="comment-list">
						<div id="comment-list-items">
							<div class="comment-list-cnt">
								<FeedbackCommentEntry v-for="comment in comments" :key="comment.id" :item="comment" />
							</div>
						</div>
					</div>
				</BaseFeedbackComments>
			</div>

			<BasePublishPostsWidget
				v-if="item.id"
				:fetch="{related_item_id: item.id, related_code: 'related', limit: 3, extra_fields: ['short_description'], response_fields: ['id', 'url_without_domain', 'category_url_without_domain', 'short_description','main_image_upload_path', 'main_image_thumbs','title','category_title']}"
				v-slot="{items: relatedPosts}">
				<div class="pd-related" v-if="relatedPosts?.length">
					<div class="pd-related-title">{{ labels.get('publish_related') }}</div>
					<div class="pd-related-items">
						<PublishIndexEntry v-for="relatedPost in relatedPosts" :key="relatedPost.id" :item="relatedPost" :short-description="true" />
					</div>
					<div class="pd-related-btn" v-if="item.root_category">
						<NuxtLink class="btn btn-white" :to="item.root_category.url_without_domain">
							<span>{{ labels.get('show_all_posts') }}</span>
						</NuxtLink>
					</div>
				</div>
			</BasePublishPostsWidget>
		</div>
	</BasePublishDetail>
</template>

<script setup>
	const labels = useLabels();
	const {formatDate} = useText();
	const {scrollTo} = useDom();
	const gtm = useGtmBB();
	const route = useRoute();

	// rates chart
	const ratesChart = reactive([]);
	for (let i = 1; i <= 5; i++) {
		ratesChart.push({
			rate: i,
			counter: 0,
		});
	}

	let itemData = ref()
	function postData(data) {
		itemData.value = data.value;
		const comments = data.value.feedback_comment_widget.items;
		Object.values(comments).forEach(el => {
			if (el.rate) {
				ratesChart[el.rate - 1].counter++;
			}
		});

		//gtm
		gtm.pageView({
			url: data?.value?.url || '',
			title: data?.value?.title || '',
		});
	}

	// show/hide comments form
	const commentsForm = ref(0);

	//gtm
	function loadProductsWidget(value) {
		const data = {list_title: 'publish - ' + itemData.value.title, list_code: 'publish -' + itemData.value.id};
		if(value.items.length) {
			gtm.viewItemList(value.items, data);
		}
	}
</script>

<style lang="less" scoped>
	.pd-tab-comment {
		margin: 40px 0 60px;

		@media (max-width: @m) {
			margin: 25px 0 40px;
		}
	}
	.pd-tab-comment-title {
		padding: 0 0 15px;
		font-size: 32px;
		line-height: 1.4;
		font-weight: 600;

		@media (max-width: @t) {
			font-size: 26px;
		}
		@media (max-width: @m) {
			font-size: 17px;
		}
	}
</style>
